/**
 * The analytics port for the authenticated app.
 *
 * Separate from `lib/browse/showAnalytics.ts`, which serves the anonymous public Show surface under
 * ADR-0061's rules (no `identify()`, memory-only persistence, a Show-scoped id that expires).
 * ADR-0061 requires the split: "authenticated analytics may identify a signed-in user in later
 * work, but that path must remain separate from the `BROWSE_ONLY` bundle and anonymous event
 * wrapper". The two wrappers never share a sink or a redaction rule, and keeping them in two files
 * makes that separation visible.
 *
 * Shaped as a port with a swappable adapter, matching the backend's hexagonal design: moving off
 * PostHog is a new sink, not an edit to every route.
 */

/** What a call site may pass. `undefined` is allowed so `{ cause: maybeMissing }` works without a
 * conditional spread at every call. */
export type AnalyticsProperties = Record<string, boolean | number | string | undefined>;

/** What a sink receives. `redactProperties` strips every `undefined`, and this type states that
 * invariant to the compiler, so an adapter can pass these to a provider whose own property type
 * rejects `undefined` without an assertion in between. */
export type RedactedProperties = Record<string, boolean | number | string>;

/** The closed event vocabulary. A union rather than a string, so a typo is a compile error and the
 * taxonomy can be read in one place.
 *
 * Navigation is not here: screens are captured automatically, so adding a route never requires
 * adding an event.
 *
 * Declare an event only in the same commit as the call site that emits it. An event no call site
 * fires looks like coverage that does not exist, and a query on it returns an empty chart that
 * cannot distinguish "nobody does this" from "nobody instrumented this". */
export const APP_EVENTS = [
  "buy_line_matched_buylist",
  "buylist_published",
  "buy_completed",
  "buy_started",
  "card_detail_load",
  // Which Find the card filter narrowed a search (Game, Set, Rarity, Card type), and from where.
  "catalog_search_filtered",
  // A first-visit coach-mark tour shown, and whether it was finished or skipped (and at which step).
  "coachmark_tour_finished",
  "coachmark_tour_started",
  // Customer view switched on or off, from any screen's eye.
  "customer_view_toggled",
  "ebay_solds_search_started",
  "fanatics_solds_search_started",
  "alt_solds_search_started",
  "marketplace_search_handoff",
  "marketplace_search_open_failed",
  "marketplace_search_feedback",
  // First run: each step shown, the finish, and a deliberate back out (TRO-2031).
  "first_run_completed",
  "first_run_left",
  "first_run_step_viewed",
  // A collection import's steps: file chosen, layout understood, confirmed, finished or failed
  // (TRO-2031).
  "import_committed",
  "import_completed",
  "import_failed",
  "import_started",
  "import_validated",
  // How dealers drive the Inventory list: Add, the Filters sheet and its chips, the layout, a
  // search, Select and Sort. Facet names and enum values only, never a figure or the search text.
  "inventory_add_opened",
  "inventory_filter_removed",
  "inventory_filters_closed",
  "inventory_filters_opened",
  "inventory_layout_changed",
  // Print or export of selected units as a PDF: sheet style and count only (TRO-1848).
  "inventory_print_export",
  "inventory_searched",
  "inventory_select_started",
  "inventory_sorted",
  "item_confirmed",
  // A Location (or Binder) created, from either place that offers it (TRO-2031).
  "location_created",
  // The paywall opened, and the Feature the person reached for (TRO-2031).
  "paywall_viewed",
  // A provider's own page opened from a queued card's price stack.
  "price_source_opened",
  // An owner's Refresh of the shop's market prices, and which screen started it.
  "prices_refresh_started",
  // A subscription purchase from intent to the plan arriving; never a price (TRO-2031).
  "purchase_cancelled",
  "purchase_completed",
  "purchase_failed",
  "purchase_started",
  "sale_completed",
  "sale_started",
  "scan_abandoned",
  // Which lens and picture size the device offered, and which one was chosen (TRO-883).
  "scan_camera_ready",
  // A queued card's condition corrected, which re-prices it.
  "scan_condition_changed",
  // The graded ladder opened from a queued card.
  "scan_graded_values_opened",
  // Batched live edge-detection timing; emitted only when live guidance is enabled (TRO-1024).
  "scan_guidance_sample",
  // A card taken out of the scan queue, and what state it was in.
  "scan_line_removed",
  // The Location a scan batch files into was set or cleared.
  "scan_location_set",
  // The operator's own Mkt Price set on a queued card, and dropped back to the provider's.
  "scan_market_overridden",
  "scan_market_override_reset",
  // "Wrong card?" opened, and the match the operator swapped to: the recognizer's own miss rate.
  "scan_match_change_opened",
  "scan_match_changed",
  // A queued card opened in its detail sheet, and paged to a neighbour by swipe or arrow.
  "scan_queue_card_opened",
  "scan_queue_card_paged",
  // The ready queue handed to Buy, Sell or Trade instead of filed as stock.
  "scan_queue_sent",
  "scan_resolved",
  // The source comparison under a queued card's Mkt Price opened.
  "scan_sources_compared",
  "scan_started",
  // Which sealed product a barcode line was booked as, its rank in the resolver's list, and how
  // long the choice took (TRO-1534).
  "sealed_pick",
  // Where an operator left a barcode line for the sealed search (TRO-1534).
  "sealed_search_opened",
  // A card put on one side of the trade window, by which route (stock search, label or card scan,
  // catalog search, a plain item, the scan queue) and whether it was a slab.
  "trade_card_added",
  // A card on the table moved to the other side: how often a card lands on the wrong side.
  "trade_card_moved",
  // A card's editor opened from its tile.
  "trade_card_opened",
  // A card taken off, from its tile's one-tap remove or from its editor.
  "trade_card_removed",
  "trade_completed",
  // The net retyped through its pencil, which re-credits the customer's cards.
  "trade_net_edited",
  // Both sides cleared with Reset.
  "trade_reset",
  // The Settle sheet opened: a trade ready to agree, whether or not it is recorded.
  "trade_settle_opened",
  // Both sides swapped at once with ⇅: how many cards moved, and how many had to stay.
  "trade_sides_swapped",
  "trade_started",
] as const;

export type AppEvent = (typeof APP_EVENTS)[number];

export type AnalyticsSink = {
  capture(event: string, properties: RedactedProperties): void;
  screen(routeName: string, properties: RedactedProperties): void;
  identify(distinctId: string, properties: RedactedProperties): void;
  setOnce(properties: RedactedProperties): void;
  reportError(error: unknown, properties: RedactedProperties): void;
  group(groupType: string, groupKey: string, properties: RedactedProperties): void;
  reset(): void;
};

export type Analytics = {
  track(event: AppEvent, properties?: AnalyticsProperties): void;
  screen(routeName: string, properties?: AnalyticsProperties): void;
  identifyUser(userPublicId: string, properties?: AnalyticsProperties): void;
  recordFirstTouch(properties: AnalyticsProperties): void;
  reportError(error: unknown, properties?: AnalyticsProperties): void;
  identifyWorkspace(workspacePublicId: string, properties?: AnalyticsProperties): void;
  signedOut(): void;
};

/**
 * Property names redacted by default, matched case-insensitively as substrings.
 *
 * A denylist rather than an allowlist because the event vocabulary above is closed but its
 * properties are open, and an allowlist that must be extended for every new property tends to get
 * widened to `*` under deadline. The list extends the promise ADR-0061 makes for the anonymous
 * surface to the authenticated one: bounded counts, catalog ids and enum values, never a Vendor's
 * private figures or a customer's identity. Generated marketplace queries have an event-scoped
 * exception in `redactProperties` so search quality can be checked.
 *
 * Follows `removeSearchText` in `showAnalyticsClient.web.ts`: strip once at the wrapper, where a
 * test can see it, instead of trusting every call site to remember.
 */
const FORBIDDEN_PROPERTY_FRAGMENTS = [
  "address",
  "amount",
  "ask",
  // Money in this codebase is often denominated in cents (`toCents`, `centsToStr`, `extCents`,
  // `cashDeltaCents`, `outTotalCents`), and the unit is the reliable marker, not the noun. No
  // other fragment here catches a name like `cashDeltaCents`.
  //
  // "cents" rather than "cash": a boolean named `has_cash_delta` says whether money moved, not how
  // much, and is a legitimate property. Banning the noun would strip it while still missing
  // `extCents`.
  "cents",
  "basis",
  "cost",
  "email",
  "margin",
  "note",
  "phone",
  "price",
  "profit",
  "query",
  "revenue",
  "search",
  "total",
  "value",
];

/** A string property long enough to be free text rather than an id or an enum. Catalog ids and
 * UUIDs sit well under this; a card name, a customer note or a typed query does not. */
const MAX_STRING_LENGTH = 64;

export function isForbiddenPropertyName(name: string): boolean {
  const lowered = name.toLowerCase();
  return FORBIDDEN_PROPERTY_FRAGMENTS.some((fragment) => lowered.includes(fragment));
}

/**
 * Drops anything the wrapper promises never to send. Applied to every sink call, so a new call site
 * cannot opt out of it by forgetting.
 *
 * Undefined values are dropped too, so an absent property and a property explicitly set to
 * `undefined` do not produce two different shapes in the provider's schema.
 */
export function redactProperties(
  properties: AnalyticsProperties = {},
  event?: AppEvent,
): RedactedProperties {
  const safe: RedactedProperties = {};
  for (const [name, value] of Object.entries(properties)) {
    if (value === undefined) continue;
    // Preserve the generated marketplace query so a search can be reproduced (TRO-2041).
    if (
      (event === "ebay_solds_search_started" || event === "fanatics_solds_search_started"
        || event === "alt_solds_search_started"
        || event === "marketplace_search_handoff"
        || event === "marketplace_search_open_failed"
        || event === "marketplace_search_feedback")
      && name === "search_query" && typeof value === "string"
    ) {
      safe[name] = value;
      continue;
    }
    if (isForbiddenPropertyName(name)) continue;
    if (typeof value === "string" && value.length > MAX_STRING_LENGTH) continue;
    safe[name] = value;
  }
  return safe;
}

/** The workspace group type. PostHog's group analytics keys insights by this, which turns monthly
 * active users into monthly active workspaces, the number that matches how Trovie is sold: two
 * staff sharing one shop are one customer, not two. */
export const WORKSPACE_GROUP_TYPE = "workspace";

/**
 * The screen name for a route, built from expo-router's segments rather than its pathname.
 *
 * `usePathname()` resolves dynamic parameters (`/shows/8f2c.../vendors/41b9...`), which would
 * create one screen name per Show and publish workspace and Show ids as navigation data.
 * `useSegments()` keeps them as patterns (`[showId]`, `[participationId]`), the "route, not URL"
 * shape a Paths insight needs. expo-router's own Head implementation relies on the same property:
 * "agnostic of URLs to ensure dynamic parameters are not considered".
 *
 * Group segments (`(app)`, `(tabs)`) are kept, because they record which navigator a screen sits
 * under and contain nothing dynamic.
 */
export function routeNameFromSegments(segments: readonly string[]): string {
  return segments.length === 0 ? "/" : `/${segments.join("/")}`;
}

export function createAnalytics(sink: AnalyticsSink): Analytics {
  return {
    track(event, properties) {
      sink.capture(event, redactProperties(properties, event));
    },
    screen(routeName, properties) {
      // A distinct sink verb rather than a capture with a special event name: PostHog, Amplitude
      // and Mixpanel all model a screen view separately from a custom event, so the adapter maps
      // it, not the port.
      sink.screen(routeName, redactProperties(properties));
    },
    identifyUser(userPublicId, properties) {
      sink.identify(userPublicId, redactProperties(properties));
    },
    /** Written with `$set_once` semantics by the adapter, so the first value wins permanently.
     * Attribution that a later visit could overwrite would answer "where were they last" while
     * claiming to answer "where did they come from". */
    recordFirstTouch(properties) {
      sink.setOnce(redactProperties(properties));
    },
    /** For an error the app caught and handled, such as a render crash or a failed mutation worth
     * counting. The properties are redacted like everything else; the error's own message and
     * stack are not, because redacting them would make them useless. That is the unavoidable cost
     * of error tracking, and the reason console capture is off in the adapter. */
    reportError(error, properties) {
      sink.reportError(error, redactProperties(properties));
    },
    identifyWorkspace(workspacePublicId, properties) {
      sink.group(WORKSPACE_GROUP_TYPE, workspacePublicId, redactProperties(properties));
    },
    signedOut() {
      sink.reset();
    },
  };
}

/** The default wherever the adapter is absent: the `BROWSE_ONLY` bundle, tests, and any build
 * without a configured key. Analytics failure must never block a surface (ADR-0061), and a no-op
 * with the same type holds that without a null check at every call site. */
export function disabledAnalytics(): Analytics {
  return createAnalytics({
    capture() {},
    screen() {},
    identify() {},
    setOnce() {},
    reportError() {},
    group() {},
    reset() {},
  });
}
